import React, { useEffect, useState, useRef } from 'react';
import { Box, Link, Alert } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { jwtDecode } from 'jwt-decode';
import { AppDispatch, RootState } from '../../store/store';
import { updateVerificationCode, clearVerificationError } from '../../features/user/userSlice';
import { matchOrg, resendVerificationCode, verifyEmail } from '../../features/user/utils';
import { useNavigate } from 'react-router-dom';
import { Button } from '../new/Button';
import { TextField } from '../new/inputs/TextField';
import { Typography } from '../new/Typography';

interface VerificationCodeFormProps {
  origin: 'signin' | 'signup' | 'reset-email';
}

const VerificationCodeForm = ({ origin }: VerificationCodeFormProps) => {
  const navigate = useNavigate();

  const dispatch: AppDispatch = useDispatch();

  const formData = useSelector((state: RootState) => state.user.verificationForm);
  const errors = useSelector((state: RootState) => state.user.verificationFormError);
  const isVerifyingEmail = useSelector((state: RootState) => state.user.isVerifyingEmail);

  const [error, setError] = useState<string>('');
  const [timer, setTimer] = useState<number>(60);
  const [isResendDisabled, setIsResendDisabled] = useState<boolean>(true);
  const hasTracked = useRef(false);

  useEffect(() => {
    // Clear any stale error from a previous verification attempt.
    dispatch(clearVerificationError());
  }, []);

  useEffect(() => {
    // to ensure the event only tracked once
    if (hasTracked.current) return;
    hasTracked.current = true;
    const signupToken = localStorage.getItem('signupToken')
    if (signupToken) {
      const decoded: any = jwtDecode(signupToken)
      //@ts-ignore
      window.pendo && window.pendo.initialize({
        visitor: {
          id: decoded.customerId,
          email: decoded.email,
          registrationDayTime: decoded.createdAt,
        },
      })
      //@ts-ignore
      window.pendo && window.pendo.track("registration-verify-email", {
        visitor: {
          id: decoded.customerId,
          email: decoded.email,
          registrationDayTime: decoded.createdAt,
        },
      });
    }
  }, []);

  useEffect(() => {
    let countdown: NodeJS.Timeout;
    if (isResendDisabled) {
      countdown = setInterval(() => {
        setTimer((prevTimer) => {
          if (prevTimer <= 1) {
            clearInterval(countdown);
            setIsResendDisabled(false);
            return 0;
          }
          return prevTimer - 1;
        });
      }, 1000);
    }

    return () => clearInterval(countdown);
  }, [isResendDisabled]);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    if (!formData.code) {
      setError('Verification code is required');
    } else {
      setError('');
      dispatch(verifyEmail(origin)).then((result) => {
        if (verifyEmail.fulfilled.match(result)) {
          dispatch(matchOrg());
          navigate('/auth/organization');
        }
      });
    }
  };

  const handleResendCode = () => {
    setIsResendDisabled(true);
    setTimer(60);
    dispatch(resendVerificationCode(origin));
  };

  return (
    <Box component="form" noValidate onSubmit={handleSubmit} sx={{ width: '50%', mt: 1 }}>
      <Typography sx={{ fontSize: '0.875rem', marginBottom: '1.25rem', textAlign: 'center' }}>
        Enter the authorization code from your email.
      </Typography>
      <TextField
        id="verificationCode"
        name="verificationCode"
        placeholder="Add..."
        required
        value={formData.code}
        onChange={(e) => dispatch(updateVerificationCode(e.target.value))}
        error={error}
        helperText={error}
        label="Authorization Code"
      />
      <Button onClick={handleSubmit} disabled={isVerifyingEmail} type="primary" sx={{ mt: 1, mb: 2 }}>
        {isVerifyingEmail ? 'Veryfing Email...' : 'Verify'}
      </Button>
      {errors.requestError && <Alert severity="error">{errors.requestError}</Alert>}
      {!errors.requestError && formData.verificationCodeResend && (
        <Alert severity="success">A new verification code has been successfully sent. Please check your email!</Alert>
      )}
      <Box
        sx={{
          textAlign: 'center',
          mt: 2,
        }}
      >
        <Link
          component="button"
          type="button"
          variant="body2"
          onClick={handleResendCode}
          disabled={isResendDisabled}
          sx={{
            cursor: isResendDisabled ? 'not-allowed' : 'pointer',
            color: isResendDisabled ? 'text.disabled' : 'primary.main',
          }}
        >
          Resend code {isResendDisabled && `(${timer}s)`}
        </Link>
      </Box>
    </Box>
  );
};

export default VerificationCodeForm;
